import {User} from "@/models/User.model";
import {DateTime, Interval} from "luxon";
import { Classroom } from "@/models/Classroom.model";
import { Curriculum } from "@/models/Curriculum.model";
import config from '@/config/app.config';

export class Organisation
{
	public id ?: string;
	public name ?: string;
	public locale ?: string;
	public country ?: string;
	public subscription_plan ?: string;
	public subscription_type ?: string;
	public hubspot_id ?: string;
	public created_at ?: Date;
	public features : string[] = [];
	public authenticated ?: boolean = false;
	public owner: User | null = null;
	public termination_date ?: DateTime | null;
	public authenticatedUserRole ?: string;
	public classrooms : Classroom[] = [];
	public active: boolean = true;
	public curriculumId ?: string = undefined;
	public agenda_settings ?: {
		include_weekends: boolean,
		school_hours_start ?: string,
		school_hours_end ?: string,
	} = undefined;
	public schoolyear_start ?: DateTime;
	public pupil_cloud_link_enabled ?: boolean = false;
	public type: 'school' | 'group' = 'school';
	public schoolGroup: { id: string, name: string } | null = null;

	static mapFromServer(data: any) {
		const organisation = new Organisation();
		organisation.setFromServerData(data);

		return organisation;
	}

	setFromServerData(data: any)
	{
		this.id = data.id;
		this.name = data.name;
		this.locale = data.locale;
		this.country = data.country;
		this.subscription_plan = data.subscription_plan;
		this.subscription_type = data.subscription_type;
		this.hubspot_id = data.hubspot_id;
		this.created_at = new Date(data.created_at);
		this.authenticated = data.authenticated;
		this.active = data.active;

		if (data.termination_date) {
			this.termination_date = DateTime.fromISO(data.termination_date);
		} else {
			this.termination_date = null;
		}

		if (data.features) {
			this.features = data.features;
		}

		if (data.owner) {
			this.owner = User.mapFromServer(data.owner);
		}

		if (data.user_role) {
			this.authenticatedUserRole = data.user_role;
		}

		if (data.classrooms) {
			this.classrooms = data.classrooms.map(Classroom.createFromApiData);
			this.classrooms.forEach(classroom => {
				classroom.organisation = this;
			});
		}

		if (data.curriculum) {
			this.curriculumId = data.curriculum;
		}

		if (data.agenda_settings) {
			this.agenda_settings = data.agenda_settings;
		}

		if (data.schoolyear_start) {
			this.schoolyear_start = DateTime.fromISO(data.schoolyear_start);
		}

		if(data.pupil_cloud_link_enabled) {
			this.pupil_cloud_link_enabled = data.pupil_cloud_link_enabled;
		}

		this.type = data.type ?? 'school';
		this.schoolGroup = data.school_group ?? null;
	}

	getServerData() {
		const out = {
			name: this.name,
			locale: this.locale,
			country: this.country,
			subscription_plan: this.subscription_plan,
			subscription_type: this.subscription_type,
			hubspot_id: this.hubspot_id,
			termination_date: this.termination_date?.toFormat('yyyy-MM-dd'),
			curriculum: this.curriculumId,
			agenda_settings: this.agenda_settings,
			pupil_cloud_link_enabled: this.pupil_cloud_link_enabled,
			type: this.type,
			school_group_id: this.type === 'group' ? null : (this.schoolGroup?.id ?? null),
		};

		return out;
	}

	clone(): Organisation {
		let clonedInstance = Object.assign(
			Object.create(
				Object.getPrototypeOf(this)
			),
			this
		);
		return clonedInstance;
	}

	hasId() {
		return this.id !== undefined;
	}

	isPaying() {
		if (!this.subscription_type) {
			return false
		}
		return this.subscription_type === 'paying';
	}

	isNonCommercialAccount() {
		if (!this.subscription_type) {
			return false
		}
		return this.subscription_type !== 'paying' && this.subscription_type !== 'trial';
	}

	getCurrentSchoolYearInterval(): Interval {
		return this.getSchoolYearInterval(0);
	}

	/**
	 * The school year a number of years away from the current one, as configured for this
	 * organisation (0 = current, -1 = previous, 1 = next). Invalid when no school year start is set.
	 */
	getSchoolYearInterval(offset: number): Interval {
		if (!this.schoolyear_start) {
			return Interval.invalid('No school year start date set');
		}

		const start = this.schoolyear_start.plus({years: offset});
		return Interval.fromDateTimes(
			start,
			start.plus({years: 1}).minus({days: 1})
		);
	}

	get curriculum() {
		if (!this.curriculumId) {
			return null;
		}
		return new Curriculum(this.curriculumId);
	}

	get trialAboutToExpire(): boolean {
		if (this.subscription_type !== 'trial') {
			return false;
		}

		if (!this.termination_date) {
			return false;
		}

		return this.termination_date.diffNow('days').days <= config.TRIAL_EXPIRATION_WARNING_DAYS;
	}

	get salesEmail(): string {
		return 'david@smartsymbols.com';
	}
}
